/*
 * A dedicated API for interacting with EDS and EMS. Supports all CRUD operations and uses useSWR.
 */
import * as Sentry from '@sentry/nextjs'
import useSWR, { Key } from 'swr'
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation'

import { useErrorContext } from '../components/userFeedback/useErrorMessage'
import { escalationMessage, standardErrorMessage } from '../utils/constants/constants'
import hasPermissions, { permissions } from '../utils/validation/hasPermissions'
import { useKeycloak } from './keycloakContextProvider'

type RequestOptions = Record<string, string | number | string[] | Record<string, number>>

export enum CrudMethod {
    get = 'GET',
    create = 'POST',
    edit = 'PUT',
    delete = 'DELETE',
}

export interface RequestParams {
    [key: string]: string | number | boolean | undefined
}

interface DataResponse<T> {
    data?: T
    fetchError?: Error
    isLoading: boolean
    isValidating?: boolean
    /** Triggers a re-fetch against the current key. Backed by SWR's mutate. */
    mutate?: () => Promise<T | undefined>
}

interface MutatorResponse<T, Error> {
    data?: T
    mutateError?: Error
    isMutating: boolean
    trigger: (extraArgument?: any, options?: SWRMutationConfiguration<T, Error, Key, never>) => Promise<T | undefined>
}

const errorMessage = (statusCode: number) => {
    switch (statusCode) {
        case 400:
            return {
                message: `Bad Request Error, try refreshing the page. If this issue persists, p${escalationMessage.slice(1)}`,
            }
        case 401:
            return {
                message: `You do not have access to this right now, try refreshing the page. If this issue persists, p${escalationMessage.slice(1)}`,
            }
        case 403:
            return {
                message: `You do not have the right permission to access this. ${standardErrorMessage}`,
            }
        default:
            return { message: `Error - ${standardErrorMessage}` }
    }
}

const extractError = (error: Error | { errors: any }) => {
    if (typeof error === 'string') {
        return error
    }
    if ('message' in error) {
        return error.message
    } else if ('errors' in error) {
        let errorMessage = ''
        //Based on extracting error messages from BatchErrorResponse defined in EMS
        for (const key in error.errors) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/restrict-template-expressions
            errorMessage += ` ${error.errors[key]}`
        }
        return errorMessage
    } else {
        return 'Undefined Error has occurred'
    }
}

const fetcher = async (url: string, bearerTokenUsed: string, tenant: string, showError: (error: Error) => void) => {
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${bearerTokenUsed}`,
            'X-Tenant-ID': `${tenant}`,
            'Content-Type': 'application/json',
        },
    })
    if (!response.ok) {
        const error = (await response.json().catch(() => {
            return errorMessage(response.status)
        })) as Error
        const fetchError = new Error(`Error: ${extractError(error)} | StatusCode: ${response.status}`)
        Sentry.captureException(fetchError)
        showError(fetchError)
        throw fetchError
    }
    // It's cast to Generic T later by SWR
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response.json()
}

const formatParams = (params: RequestParams) =>
    params
        ? Object.entries(params)
              .map(([key, value]) => `${key}=${value}`)
              .join('&')
        : ''

const urlAppended = (url: string, queryParams?: RequestParams) =>
    `${url}${queryParams ? `?${formatParams(queryParams)}` : ''}`

export function useFetch<T>(
    url: string,
    queryParams?: RequestParams,
    nameParams?: string,
    stopFetch?: boolean,
    errorRetryCount: number = 5,
    keepPreviousData?: boolean
): DataResponse<T> {
    const { tenant } = useKeycloak()
    const token = sessionStorage.getItem(`token-${tenant}`)
    const fullUrl: string = nameParams ? `${url}${nameParams}` : url
    const { showError } = useErrorContext()

    const { data, error, isLoading, isValidating, mutate } = useSWR<T, Error>(
        !stopFetch ? [urlAppended(fullUrl, queryParams), token] : null,
        ([fullUrl, token]: string) => fetcher(fullUrl, token, tenant, showError),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateIfStale: false,
            errorRetryCount: errorRetryCount,
            keepPreviousData: keepPreviousData,
        }
    )

    return { data, fetchError: error, isLoading, isValidating, mutate: () => mutate() }
}

export function useDataMutator<T>(url: string, method: CrudMethod): MutatorResponse<T, Error> {
    const { roles } = useKeycloak()
    const isDefaultUser = !hasPermissions(roles, permissions.updateMonitoring)
    const { tenant } = useKeycloak()
    const token = sessionStorage.getItem(`token-${tenant}`)
    const { showError } = useErrorContext()

    async function mutateData(url: string, { arg }: RequestOptions): Promise<T> {
        if (isDefaultUser && method === CrudMethod.delete) {
            const forbiddenAccessError = new Error(
                'Forbidden access, experimentation-admin role is required to send Delete requests'
            )
            Sentry.captureException(forbiddenAccessError)
            throw forbiddenAccessError
        }
        const response = await fetch(url, {
            method: method,
            headers: {
                Authorization: `Bearer ${token ?? ''}`,
                'X-Tenant-ID': `${tenant}`,
                'Content-Type': 'application/json',
            },
            body: `${JSON.stringify(arg, (k, v) => (v === undefined ? null : v) as unknown)}`,
        })
        if (!response.ok) {
            const error = (await response.json().catch(() => {
                return errorMessage(response.status)
            })) as Error
            const mutateDataError = new Error(`Error: ${extractError(error)} | StatusCode: ${response.status}`)
            Sentry.captureException(mutateDataError)
            showError(mutateDataError)
            throw mutateDataError
        }
        //Adding catch here in case of DELETE request, DELETE will not return anything and cause an error on .json() call
        return (await response.json().catch((error) => {
            console.error(error)
            return 'No Content'
        })) as T
    }

    const { trigger, error, data, isMutating } = useSWRMutation<T, Error>(url, mutateData)

    return { trigger, mutateError: error, data, isMutating }
}
